/**
 * Post-sign-in redirect guard (#3544).
 *
 * The magic-link / SMS `redirect_url` (#830) is stored server-side and echoed
 * back at consume/verify, so treat it as untrusted input. React Router 7
 * collapses `//` and `/\` runs before pushState, but a tab or newline inside
 * the path (`/\t/evil.example`) survives that, the browser's URL parser strips
 * it, pushState rejects the now cross-origin `//evil.example`, and React
 * Router falls back to `window.location.assign()`: an off-site redirect right
 * after a trusted sign-in.
 *
 * URL-boundary rule (Rule 52): exactly one leading `/`, and no backslash,
 * whitespace or control character (C0, DEL, C1) anywhere. Mirrors `isAppRelativePath` in
 * backend/src/schemas/index.ts, which rejects the same values at request time;
 * both are pinned to the same accept/reject vectors. Native has its own,
 * stricter allowlist (`resolveIntentPath`, mobile/src/lib/deepLinkIntent.ts).
 */
export function isSafeRedirectPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === '\\' || /\s/.test(ch) || code < 0x20 || (code >= 0x7f && code <= 0x9f)) return false;
  }
  return true;
}

/** The value if it is a safe app-relative path, otherwise null. */
export function toSafeRedirectPath(value: unknown): string | null {
  return isSafeRedirectPath(value) ? value : null;
}
